import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import LcResolutionBoard from '../../components/scheduling/LcResolutionBoard';
import { useAdditionalFieldSelection } from '../../hooks/useAdditionalFields';
import { useToast } from '../../contexts/ToastContext';
import schedulingService from '../../services/scheduling.service';
import { LcResolutionItem } from '../../types/scheduling';
import { matchLcToCurrentUser } from '../../utils/matchLcToCurrentUser';

// Matches AtRiskMonitor / DispatchDashboard / SmartDispatch / DriverStatusMonitor.
const REFRESH_INTERVAL = 30000;

/**
 * Operational TTs & Service Failures work queue: open trouble tickets +
 * incomplete service failures, scoped by LC (defaults to the current user).
 * The editable table, save pipeline, and modals live in the shared
 * LcResolutionBoard; this wrapper owns the LC filter, fetch, and auto-refresh.
 */
// One key for the layout AND the added-column selection — they belong to the same
// grid, and the board takes it as a prop.
const SETTINGS_KEY = 'scheduling-tts-and-service-failures-v2';

export default function TTsAndServiceFailures() {
  const fieldSelection = useAdditionalFieldSelection(SETTINGS_KEY);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { accounts } = useMsal();
  const { showToast } = useToast();

  // ── Ticket selection from the LC Dashboard Tickets panel ──────────────────
  // The dashboard scopes the queue to exactly its bucket's trouble tickets (its
  // customer / bill-to / etc. filters can't all be reproduced by the page's LC
  // filter, so it passes recnos). Resolved once, preferring the localStorage
  // handoff (?ttSel=<token> — no URL-length ceiling) and falling back to ?ttRecnos
  // in the URL. A local Set drives the filter so the user can clear it.
  const initialTtSelection = useMemo<{ recnos: number[]; label: string }>(() => {
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('ttSel');
    if (token) {
      try {
        const parsed = JSON.parse(localStorage.getItem(`lc-tt-drill-${token}`) || 'null');
        const recnos = (parsed?.recnos as unknown[] | undefined) || [];
        return {
          recnos: recnos.map((n) => Number(n)).filter((n) => !Number.isNaN(n)),
          label: (parsed?.label as string) || '',
        };
      } catch { /* fall through to empty */ }
      return { recnos: [], label: '' };
    }
    const raw = sp.get('ttRecnos');
    return {
      recnos: (raw ? raw.split(',') : []).map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)),
      label: sp.get('ttLabel') || '',
    };
  }, []);
  const [selectedTtRecnos, setSelectedTtRecnos] = useState<Set<number>>(() => new Set(initialTtSelection.recnos));
  const ttSelectionLabel = initialTtSelection.label;
  const hasTtSelection = selectedTtRecnos.size > 0;

  // ── LC filter ──────────────────────────────────────────────────────────
  // "LC" (Logistics Coordinator) is the current label; the DB column is still
  // `o.csr` and the API param is still `csr` (both pre-date the rename).
  const [lcOptions, setLcOptions] = useState<MultiSelectOption[]>([]);
  const [lcFilter, setLcFilter] = useState<string[]>([]);
  const [lcFilterInitialized, setLcFilterInitialized] = useState(false);

  // Load LC options on mount; default the filter to the current user once we
  // have both pieces of data.
  useEffect(() => {
    let cancelled = false;
    schedulingService
      .getFilterOptions()
      .then((res) => {
        if (cancelled) return;
        const opts = res.logistics_coordinators.map((lc) => ({
          value: lc.value,
          label: lc.label,
        }));
        setLcOptions(opts);
        if (!lcFilterInitialized) {
          // When drilled in with a ticket selection, fetch across ALL LCs (leave the
          // filter empty) so every selected recno is present regardless of its LC —
          // the recno filter does the scoping. Otherwise default to the current user.
          // Match on the full-name label (like the Scheduling grid) — the option
          // value is the V1 csr short username, which almost never equals the email
          // local-part, so value-based matching silently fell back to All LCs.
          if (!hasTtSelection) {
            const match = matchLcToCurrentUser(opts, accounts[0]);
            if (match) setLcFilter([match.value]);
          }
          setLcFilterInitialized(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLcFilterInitialized(true);
      });
    return () => { cancelled = true; };
  }, [accounts, lcFilterInitialized, hasTtSelection]);

  // ── Row fetch ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<LcResolutionItem[]>([]);
  // Starts loading: the first fetch waits on the LC default, and until it
  // lands the grid must show a spinner, not "No data available".
  const [loading, setLoading] = useState(true);
  // Single in-flight fetch — aborting the previous one prevents a stale
  // background refresh from racing a fresh filter-change fetch.
  const fetchAbortRef = useRef<AbortController | null>(null);

  const fetchRows = useCallback(async (isBackgroundRefresh = false) => {
    // Wait until we've at least attempted to set a default filter so the first
    // request reflects the user's intended scope.
    if (!lcFilterInitialized) return;

    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    if (!isBackgroundRefresh) setLoading(true);
    try {
      const list = await schedulingService.getLcResolution(
        lcFilter, controller.signal, fieldSelection.additionalFields,
      );
      setRows(list);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch LC resolution queue:', err);
      // Suppress background-refresh toasts so a transient blip doesn't spam the
      // user; the next tick will retry.
      if (!isBackgroundRefresh) {
        showToast({ type: 'error', message: 'Failed to load TTs & service failures' });
      }
    } finally {
      // Only the request still in flight owns the spinner — an aborted fetch
      // must not switch it off while its replacement is still loading.
      if (!isBackgroundRefresh && fetchAbortRef.current === controller) setLoading(false);
    }
  }, [lcFilter, lcFilterInitialized, showToast, additionalFieldsKey]);

  // The saved selection is part of the request, so hold the first fetch for it —
  // going early just runs the query twice.
  useEffect(() => {
    if (!fieldSelection.ready) return;
    void fetchRows();
  }, [fetchRows, fieldSelection.ready]);
  useEffect(() => () => fetchAbortRef.current?.abort(), []);

  // When drilled in from the dashboard, narrow the queue to the selected tickets.
  const displayedRows = useMemo(
    () => (hasTtSelection
      ? rows.filter((r) => r.troubleticketrecno != null && selectedTtRecnos.has(r.troubleticketrecno))
      : rows),
    [rows, hasTtSelection, selectedTtRecnos],
  );

  return (
    <LcResolutionBoard
      rows={displayedRows}
      loading={loading}
      onRefetch={(opts) => { void fetchRows(opts?.background ?? false); }}
      title="TTs & Service Failures"
      subtitle="Open trouble tickets and incomplete service failures. Right-click + drag a column to bulk edit."
      // -v2: bumped to drop stale persisted sort (old troubleticketrecno default)
      // so the chronological backend order becomes the default. See LcResolutionBoard.
      settingsKey={SETTINGS_KEY}
      fieldSelection={fieldSelection}
      // Pasting a list of pros here reconciles against a deliberately narrow queue, and
      // the two things it excludes are exactly the two a user would assume were bugs.
      listFilterScopeNote={
        'This queue holds NEW and PENDING tickets plus incomplete service failures, for '
        + 'the LCs selected above. A pro whose ticket is already resolved, or belongs to '
        + 'another LC, will read as unmatched — look for it on the TTs & Service Failures '
        + 'report instead.'
      }
      autoRefreshMs={REFRESH_INTERVAL}
      highlightDriverTicketY
      showResolveColumn
      filterBar={
        <>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">LC</label>
          <MultiSelect
            options={lcOptions}
            value={lcFilter}
            onChange={setLcFilter}
            placeholder="All LCs"
            showClearOption
            clearOptionLabel="All LCs"
            enableSearch
            searchPlaceholder="Search LCs..."
            size="sm"
            className="min-w-[240px]"
          />
          {hasTtSelection && (
            <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/40 border border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 whitespace-nowrap">
              {`From dashboard${ttSelectionLabel ? ` · ${ttSelectionLabel}` : ''}: ${selectedTtRecnos.size}`}
              <button
                type="button"
                aria-label="Clear dashboard ticket selection"
                onClick={() => setSelectedTtRecnos(new Set())}
                className="font-semibold hover:underline"
              >
                ✕
              </button>
            </span>
          )}
        </>
      }
    />
  );
}
